import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Search } from "lucide-react";
import { API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "./ProductCard";

const CATEGORIES = ["Tutte", "Wrangler", "Freddy", "Nautica", "Rifle", "Enrico Coveri", "Ellesse"];
const EASE = [0.22, 1, 0.36, 1];

export default function Catalog() {
  const { query, setQuery } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("Tutte");

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data.products))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter(
      (p) =>
        (category === "Tutte" || p.category === category) &&
        (!q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
    );
  }, [products, category, query]);

  return (
    <section id="catalogo" className="border-t border-black/10 bg-white" data-testid="catalog-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta">Catalogo ingrosso</p>
            <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl lg:text-6xl">
              Il Catalogo
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-ink/55">
              Vendita esclusivamente a box interi per singolo colore: 12 paia per Wrangler,
              Nautica, Enrico Coveri ed Ellesse, 10 paia per Freddy e Rifle. Scegli colore e
              assortimento taglie sulla scheda.
            </p>
          </div>
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca modello o SKU…"
              className="h-11 w-full border border-black/15 bg-paper pl-10 pr-3 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
              data-testid="catalog-search-input"
            />
          </div>
        </motion.div>

        <div className="mb-12 flex flex-wrap gap-2" data-testid="catalog-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                category === c
                  ? "bg-ink text-white"
                  : "border border-black/15 bg-white text-ink/60 hover:border-ink hover:text-ink"
              }`}
              data-testid={`filter-${c.toLowerCase().replace(/ /g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse border border-black/10 bg-paper" />
            ))}
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-grid">
            <AnimatePresence mode="popLayout">
              {filtered.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {!loading && filtered.length === 0 && (
          <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.24em] text-ink/45" data-testid="catalog-empty">
            Nessun modello trovato per «{query}»
          </p>
        )}
      </div>
    </section>
  );
}
